import { BusinessSectionNode } from "../../interfaces/BusinessSectionObject";
import { ProductDetailsModal } from "../../interfaces/ProductObject";

/**
 * Tree-utility and AI-name-resolution logic for the business section tree —
 * split out of services/converters/backendToUi/businessSectionConvertor.ts,
 * which is now limited to its actual job (reshaping the raw wire response
 * into BusinessSectionNode[]). Everything here operates on an
 * already-converted tree and isn't "converting" anything, so it belongs
 * alongside the other section helpers (BusinessSectionsHelper.ts,
 * ProductSectionHelper.ts) instead.
 */

/**
 * A circuit-breaker for the recursive walks below, not a real UX limit — the
 * feature itself is unlimited-depth by design. This only guards against a
 * walk running away: a corrupted or adversarial tree (e.g. a cycle that
 * somehow slipped past isSectionOrDescendant) would otherwise recurse
 * forever / until the stack overflows. No legitimate business's category
 * tree is anywhere near this deep, so it never engages in practice.
 */
export const MAX_SECTION_TREE_DEPTH = 100;

/** Depth-first lookup by id, anywhere in the tree at any depth. */
export function findSectionById(
  tree: BusinessSectionNode[],
  sectionId: string | null | undefined,
  depth = 0
): BusinessSectionNode | undefined {
  if (!sectionId || depth > MAX_SECTION_TREE_DEPTH) return undefined;
  for (const node of tree) {
    if (node.sectionId === sectionId) return node;
    const found = findSectionById(node.children, sectionId, depth + 1);
    if (found) return found;
  }
  return undefined;
}

/**
 * Root-to-node path (inclusive) for a section id, e.g. [MixerGrinder, 500W].
 * Empty array when the id isn't found anywhere in the tree — a stale id from
 * a section that was since deleted, or a product from a different business.
 */
export function findSectionPath(
  tree: BusinessSectionNode[],
  sectionId: string | null | undefined,
  depth = 0
): BusinessSectionNode[] {
  if (!sectionId || depth > MAX_SECTION_TREE_DEPTH) return [];
  for (const node of tree) {
    if (node.sectionId === sectionId) return [node];
    const childPath = findSectionPath(node.children, sectionId, depth + 1);
    if (childPath.length > 0) return [node, ...childPath];
  }
  return [];
}

/** Display-ready "Mixer Grinder > 500W" string for a resolved path. */
export function formatSectionPath(path: BusinessSectionNode[]): string {
  return path.map((node) => node.name).join(" > ");
}

/**
 * True when `candidateId` is `ancestorId` itself, or sits anywhere in
 * `ancestorId`'s own subtree. The one check that matters before ever moving a
 * section under a new parent: without it, moving "Court" to become a child of
 * its own child "Badminton Court" would splice the tree into a cycle — a node
 * that is its own ancestor — and every recursive walk over it (rendering,
 * findSectionById, the drill-in picker) would loop or blow the stack the
 * moment it reached that node. Only the sectionId being moved needs checking
 * against its OWN descendants; a destination anywhere else in the tree
 * (including elsewhere under a different ancestor) is always safe.
 */
export function isSectionOrDescendant(
  tree: BusinessSectionNode[],
  ancestorId: string,
  candidateId: string
): boolean {
  if (ancestorId === candidateId) return true;
  const ancestor = findSectionById(tree, ancestorId);
  if (!ancestor) return false;
  const walk = (nodes: BusinessSectionNode[], depth: number): boolean => {
    if (depth > MAX_SECTION_TREE_DEPTH) return false;
    return nodes.some((n) => n.sectionId === candidateId || walk(n.children, depth + 1));
  };
  return walk(ancestor.children, 0);
}

/**
 * Resolves a free-text name pair (root name + optional child name) against
 * the real tree — case-insensitive, exact match only. For wherever an AI flow
 * can only ever output a NAME (it has no way to know a real sectionId), but
 * the product needs a real id: the model's guess is trusted only as far as it
 * actually matches something in the tree, never beyond.
 *
 * Root-then-child only — this has no way to express a match deeper than 2
 * levels, the same ceiling the legacy SECTION/SUBSECTION keys always had.
 * Falls back to the matched ROOT when `subsectionName` doesn't match any of
 * its children (a partial-credit match: the model got the top level right).
 * Returns null when even the root name doesn't match anything real — a
 * hallucinated name, or "others" — rather than guessing further; NEVER
 * creates a new section from an unmatched guess.
 */
export function resolveSectionByNames(
  tree: BusinessSectionNode[],
  sectionName: string | null | undefined,
  subsectionName?: string | null
): string | null {
  const name = sectionName?.trim().toLowerCase();
  if (!name) return null;
  const root = tree.find((n) => n.name.trim().toLowerCase() === name);
  if (!root) return null;

  const subName = subsectionName?.trim().toLowerCase();
  if (!subName) return root.sectionId;
  const child = root.children.find((c) => c.name.trim().toLowerCase() === subName);
  return child ? child.sectionId : root.sectionId;
}

/** `value` as a trimmed string, or `undefined` for null/undefined — NOT
 *  `String(value)`, which would turn a genuinely-absent value into the
 *  literal text "null"/"undefined" and have it (harmlessly, but for the
 *  wrong reason) fail to match anything in the tree. */
function readOptionalString(value: unknown): string | undefined {
  return value === null || value === undefined ? undefined : String(value);
}

/** Case-insensitive key lookup — an AI flow's key casing on `section`/
 *  `subSection` isn't guaranteed, and this is the one place that assumption
 *  is encoded, shared by every caller that needs to find (or display) one of
 *  those two legacy guessed keys rather than re-deriving the same lookup. */
export function findCaseInsensitiveKey(
  record: Record<string, unknown>,
  name: string
): string | undefined {
  return Object.keys(record).find((key) => key.toLowerCase() === name);
}

/**
 * Resolves whatever `section`/`subSection` NAME an AI flow guessed (it has no
 * way to know a real sectionId) against the real tree, and returns the draft
 * with those legacy keys replaced by a resolved `details.sectionId` — or
 * simply removed, if the guess doesn't match anything real. Shared by every
 * bulk-upload surface that hands AI-analyzed drafts off to a create payload
 * (NineSetupBulkActions, BusinessBulkUploadActions), so this resolution logic
 * exists in exactly one place.
 */
export function resolveDraftSectionId(
  draft: Record<string, unknown>,
  tree: BusinessSectionNode[]
): Record<string, unknown> {
  const details = draft.details;
  if (!details || typeof details !== "object") return draft;
  const detailsRecord: Record<string, unknown> = { ...(details as Record<string, unknown>) };

  const sectionKey = findCaseInsensitiveKey(detailsRecord, "section");
  const subSectionKey = findCaseInsensitiveKey(detailsRecord, "subsection");
  const sectionName = sectionKey ? readOptionalString(detailsRecord[sectionKey]) : undefined;
  const subSectionName = subSectionKey ? readOptionalString(detailsRecord[subSectionKey]) : undefined;
  if (sectionKey) delete detailsRecord[sectionKey];
  if (subSectionKey) delete detailsRecord[subSectionKey];

  const resolvedId = resolveSectionByNames(tree, sectionName, subSectionName);
  if (resolvedId) detailsRecord.sectionId = resolvedId;

  return { ...draft, details: detailsRecord };
}

/**
 * Computes a map of sectionId -> count of products/listings in that section
 * (including all its nested descendant subsections).
 */
export function computeSectionProductCounts(
  tree: BusinessSectionNode[],
  products?: ProductDetailsModal[] | null
): Map<string, number> {
  const directCounts = new Map<string, number>();
  (products ?? []).forEach((p) => {
    const secId = p.details?.sectionId;
    if (secId) {
      directCounts.set(secId, (directCounts.get(secId) ?? 0) + 1);
    }
  });

  const totalCounts = new Map<string, number>();

  const walk = (node: BusinessSectionNode): number => {
    let count = directCounts.get(node.sectionId) ?? 0;
    for (const child of node.children ?? []) {
      count += walk(child);
    }
    totalCounts.set(node.sectionId, count);
    return count;
  };

  (tree ?? []).forEach(walk);
  return totalCounts;
}

